import { trim } from 'lodash'

export const normalize = (str = ''): string => {
  const FROM = 'ÃÀÁÄÂÇÈÉËÊÌÍÏÎÒÓÖÔÙÚÜÛÑãàáäâèéëêìíïîòóöôùúüûñç'
  const TO = 'AAAAACEEEEIIIIOOOOUUUUNaaaaaeeeeiiiioooouuuunc'
  const mapping: { [key: string]: string } = {}

  for (let i = 0, j = FROM.length; i < j; i++) {
    mapping[FROM.charAt(i)] = TO.charAt(i)
  }

  const ret = []
  for (let i = 0, j = str.length; i < j; i++) {
    const c = str.charAt(i)

    if (Object.prototype.hasOwnProperty.call(mapping, str.charAt(i))) {
      ret.push(mapping[c])
    } else {
      ret.push(c)
    }
  }
  return ret.join('').toLowerCase()
}

export const KEY_CODES = {
  ENTER: 13,
  BACKSPACE: 8,
  ARROW_UP: 38,
  ARROW_DOWN: 40
}

export enum EKeys {
  ENTER = 'Enter',
  BACKSPACE = 'Backspace',
  ARROW_UP = 'ArrowUp',
  ARROW_DOWN = 'ArrowDown'
}

export const pad2 = (num: number): string => {
  return (num < 10 ? '0' : '') + num
}

export const hasMinChar = (value = ''): boolean => {
  return trim(value).length >= 2
}

export const generatePassword = ({ passwordLength = 14 }: { passwordLength?: number } = {}): string => {
  // Allowed characters
  const lowercase = 'abcdefghijklmnopqrstuvwxyz'
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const numbers = '0123456789'
  const specialCharacters = '!"#$%&\'()*+,-./:;<=>?@[]^_`{|}~\\¿¡¨°'

  // Ensure the password contains at least one of each category
  let password = [
    lowercase[Math.floor(Math.random() * lowercase.length)],
    uppercase[Math.floor(Math.random() * uppercase.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    specialCharacters[Math.floor(Math.random() * specialCharacters.length)]
  ]

  // Fill the rest of the password to reach { passwordlength } characters
  const allCharacters = lowercase + uppercase + numbers + specialCharacters
  for (let i = 4; i < passwordLength; i++) {
    password.push(allCharacters[Math.floor(Math.random() * allCharacters.length)])
  }

  // Shuffle the characters so they do not follow a predictable pattern
  password = password.sort(() => Math.random() - 0.5)

  return password.join('')
}

/**
 * Obfuscates an email address by replacing part of the username with asterisks.
 *
 * @param {string} email - The email address to obfuscate.
 * @returns {string} The obfuscated email address.
 * @throws {Error} Throws an error if the email is invalid.
 *
 * @example
 * // returns "e******e@domain.com"
 * const obfuscated = obfuscateEmail("example@domain.com");
 */
export const obfuscateEmail = (email: string): string => {
  // Validate the email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(email)) {
    throw new Error('Invalid email')
  }

  const [username, domain] = email.split('@')

  // Calculate the length of the username to obfuscate
  const asteriskLength = Math.max(0, username.length - 1)
  const obfuscatedUsername = username.charAt(0) + '*'.repeat(asteriskLength)

  return `${obfuscatedUsername}@${domain}`
}

export const getShortId = (id: string): string => {
  return id.substring(0, 8)
}

/**
 * Formats a phone number with an optional country code.
 *
 * @param phoneNumber - The phone number to format.
 * @param countryCode - An optional country code to include in the formatted phone number.
 * @returns The formatted phone number, optionally including the country code.
 * @example
 *  phoneNumberFormatter("1234567890", "52")
 *  //returns "(52) 1234567890"
 */
export const phoneNumberFormatter = (phoneNumber: string, countryCode?: string): string => {
  return `${countryCode ? `(${countryCode}) ` : ''}${phoneNumber}`
}

/**
 * Creates a numeric hash from a string.
 * Uses a modified djb2 algorithm with improvements to reduce collisions.
 *
 * @param str - The input string to hash
 * @param seed - Optional seed value (defaults to 5381)
 * @returns A numeric hash value
 */
export function createHash(str: string, seed = 5381): number {
  if (!str) return seed

  const prime = 33
  let hash = seed ^ str.length

  for (let i = 0; i < str.length; i++) {
    // Incorporate character code and position in string
    const char = str.charCodeAt(i)
    hash = ((hash << 5) + hash) ^ char // hash * 33 XOR char

    // Add some additional entropy based on character position
    hash = (hash + (char << i % 5) * prime) & 0xffffffff
  }

  return Math.abs(hash)
}
